/**
 * Crown01 Careers & Opportunities — public pages only.
 *
 * Flow:
 *   /careers                     list of open opportunities
 *   /careers/:slug               opportunity detail + "Apply Now"
 *   /careers/:slug/apply         application form (opp1 or opp2 schema)
 *   /careers/apply/success       confirmation
 *
 * Submissions are emailed to the Crown01 careers inbox via Resend with the
 * applicant's CV attached. No admin dashboard, no stored CVs, no public links.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/*  Shared primitives                                                          */
/* -------------------------------------------------------------------------- */

export function CareersSection({ children, className = "" }) {
  return (
    <section className={`max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-16 ${className}`}>{children}</section>
  );
}

function Eyebrow({ children, color = "var(--c-gold-deep)" }) {
  return (
    <div className="text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color }}>
      {children}
    </div>
  );
}

function PageHeader({ eyebrow, title, intro }) {
  return (
    <div className="mb-10 md:mb-14 max-w-3xl">
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h1 className="font-display text-4xl md:text-5xl lg:text-6xl mt-2" data-testid="careers-title">{title}</h1>
      {intro && <div className="mt-5 text-[color:var(--c-grey-700)] leading-relaxed text-base md:text-lg space-y-3">{intro}</div>}
    </div>
  );
}

/** Lightweight Markdown-ish renderer for opportunity body text.
 *  Supports: ## headings, **bold**, bullet lists, paragraphs. No HTML injection. */
function Markdownish({ md }) {
  const blocks = useMemo(() => {
    const lines = String(md || "").split(/\r?\n/);
    const out = [];
    let listBuf = null;
    let paraBuf = null;
    const flushPara = () => { if (paraBuf) { out.push({ k: "p", t: paraBuf.join(" ") }); paraBuf = null; } };
    const flushList = () => { if (listBuf) { out.push({ k: "ul", items: listBuf }); listBuf = null; } };
    for (const raw of lines) {
      const line = raw.trim();
      if (!line) { flushPara(); flushList(); continue; }
      if (line.startsWith("## ")) { flushPara(); flushList(); out.push({ k: "h2", t: line.slice(3) }); continue; }
      if (line.startsWith("# ")) { flushPara(); flushList(); out.push({ k: "h2", t: line.slice(2) }); continue; }
      if (/^[-*]\s+/.test(line)) { flushPara(); if (!listBuf) listBuf = []; listBuf.push(line.replace(/^[-*]\s+/, "")); continue; }
      flushList();
      if (!paraBuf) paraBuf = [];
      paraBuf.push(line);
    }
    flushPara(); flushList();
    return out;
  }, [md]);

  const renderInline = (t) => {
    // bold **text**
    const parts = t.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => (p.startsWith("**") && p.endsWith("**")) ? <strong key={i}>{p.slice(2, -2)}</strong> : <React.Fragment key={i}>{p}</React.Fragment>);
  };

  return (
    <div className="space-y-5 text-[color:var(--c-grey-700)] leading-relaxed">
      {blocks.map((b, i) => {
        if (b.k === "h2") return (<h2 key={i} className="font-display text-xl md:text-2xl text-black mt-6">{renderInline(b.t)}</h2>);
        if (b.k === "ul") return (
          <ul key={i} className="space-y-2 list-none pl-0">
            {b.items.map((it, j) => (
              <li key={j} className="flex items-start gap-2.5">
                <i className="fa-solid fa-check mt-1.5 text-[10px]" style={{ color: "var(--c-gold-deep)" }} />
                <span>{renderInline(it)}</span>
              </li>
            ))}
          </ul>
        );
        return <p key={i}>{renderInline(b.t)}</p>;
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Public: Careers list + detail + apply + success                            */
/* -------------------------------------------------------------------------- */

export function CareersPage() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/careers/opportunities`)
      .then((r) => { if (!cancelled) setItems(r.data || []); })
      .catch((e) => { if (!cancelled) setErr("Could not load opportunities. Please refresh."); });
    return () => { cancelled = true; };
  }, []);

  return (
    <CareersSection>
      <PageHeader
        eyebrow="Careers at Crown01"
        title={<>Careers &amp; Opportunities at Crown01</>}
        intro={
          <>
            <p className="font-display text-xl md:text-2xl text-black">Grow with Crown01</p>
            <p>Crown01 is a growing UK supplier of packaging, cleaning, catering and household essentials, serving both consumers and businesses across the UK.</p>
            <p>As we continue to grow our B2B, wholesale, e-commerce and online presence, we are looking for motivated people who want to grow with us.</p>
            <p className="text-[color:var(--c-grey-500)]">Explore our current opportunities below.</p>
          </>
        }
      />

      <Eyebrow>Current Opportunities</Eyebrow>
      <h2 className="font-display text-2xl md:text-3xl mt-2 mb-6" data-testid="careers-opps-heading">Open roles</h2>

      {err && <div className="rounded-md p-4 bg-[color:var(--c-red)]/10 text-[color:var(--c-red)]" data-testid="careers-error">{err}</div>}
      {items === null && !err && (
        <div className="grid md:grid-cols-2 gap-5 md:gap-6">
          <div className="h-56 rounded-2xl animate-pulse bg-[color:var(--c-grey-100)]" />
          <div className="h-56 rounded-2xl animate-pulse bg-[color:var(--c-grey-100)]" />
        </div>
      )}
      {items && items.length === 0 && (
        <div className="rounded-2xl border border-dashed p-10 text-center" style={{ borderColor: "var(--c-grey-100)" }} data-testid="careers-empty">
          <div className="font-display text-2xl mb-2">No open opportunities right now</div>
          <p className="text-[color:var(--c-grey-500)]">Please check back soon — we are always looking for motivated people.</p>
        </div>
      )}
      {items && items.length > 0 && (
        <div className="grid md:grid-cols-2 gap-5 md:gap-6" data-testid="careers-grid">
          {items.map((o) => (<OpportunityCard key={o.id} o={o} />))}
        </div>
      )}
    </CareersSection>
  );
}

function OpportunityCard({ o }) {
  const closed = o.status === "closed";
  return (
    <article
      className="rounded-2xl p-6 md:p-7 flex flex-col"
      style={{ background: "var(--c-cream)", border: "1px solid var(--c-grey-100)" }}
      data-testid={`careers-card-${o.slug}`}
    >
      {o.type && <Eyebrow>{o.type}</Eyebrow>}
      <h3 className="font-display text-xl md:text-2xl mt-2 leading-tight">{o.title}</h3>
      {o.short_description && <p className="mt-3 text-[color:var(--c-grey-700)] leading-relaxed">{o.short_description}</p>}
      <div className="mt-4 flex flex-wrap gap-2 text-[11px] uppercase tracking-wider">
        {o.location && <span className="px-2.5 py-1 rounded-full" style={{ background: "#fff", border: "1px solid var(--c-grey-100)" }}><i className="fa-solid fa-location-dot mr-1 text-[10px]" style={{ color: "var(--c-gold-deep)" }} />{o.location}</span>}
        {o.requirement && <span className="px-2.5 py-1 rounded-full" style={{ background: "#fff", border: "1px solid var(--c-grey-100)" }}><i className="fa-solid fa-circle-check mr-1 text-[10px]" style={{ color: "var(--c-gold-deep)" }} />{o.requirement}</span>}
      </div>
      <div className="mt-auto pt-6">
        <Link
          to={`/careers/${o.slug}`}
          className="inline-flex items-center justify-center w-full md:w-auto px-5 py-3 rounded-md font-semibold tracking-wide text-sm"
          style={{ background: closed ? "var(--c-grey-100)" : "var(--c-black)", color: closed ? "var(--c-grey-500)" : "#fff" }}
          data-testid={`careers-view-${o.slug}`}
        >
          {closed ? "Closed · View Details" : "View Details & Apply"}
          <i className="fa-solid fa-arrow-right ml-2 text-[11px]" />
        </Link>
      </div>
    </article>
  );
}

export function OpportunityDetailPage() {
  const { slug } = useParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/careers/opportunities/${encodeURIComponent(slug)}`)
      .then((r) => { if (!cancelled) setO(r.data); })
      .catch(() => { if (!cancelled) setErr("Opportunity not found or no longer available."); });
    return () => { cancelled = true; };
  }, [slug]);

  if (err) return (
    <CareersSection>
      <PageHeader eyebrow="Careers" title="Opportunity not found" intro={<p>{err}</p>} />
      <Link to="/careers" className="inline-flex items-center text-sm font-semibold" style={{ color: "var(--c-gold-deep)" }}>
        <i className="fa-solid fa-arrow-left mr-2 text-[11px]" />Back to Careers
      </Link>
    </CareersSection>
  );
  if (!o) return (
    <CareersSection>
      <div className="h-10 w-56 bg-[color:var(--c-grey-100)] rounded animate-pulse mb-4" />
      <div className="h-14 w-full max-w-2xl bg-[color:var(--c-grey-100)] rounded animate-pulse" />
    </CareersSection>
  );

  const closed = o.status === "closed";
  return (
    <CareersSection>
      <div className="mb-5">
        <Link to="/careers" className="inline-flex items-center text-sm font-semibold" style={{ color: "var(--c-gold-deep)" }} data-testid="back-to-careers">
          <i className="fa-solid fa-arrow-left mr-2 text-[11px]" />Back to Careers
        </Link>
      </div>
      <PageHeader eyebrow={o.type || "Opportunity"} title={o.title} intro={o.short_description && <p>{o.short_description}</p>} />

      <div className="flex flex-wrap gap-3 mb-10">
        {o.location && <span className="px-3 py-1.5 rounded-full text-xs" style={{ background: "var(--c-cream)", border: "1px solid var(--c-grey-100)" }}><i className="fa-solid fa-location-dot mr-1.5 text-[10px]" style={{ color: "var(--c-gold-deep)" }} /><strong>Location:</strong> {o.location}</span>}
        {o.requirement && <span className="px-3 py-1.5 rounded-full text-xs" style={{ background: "var(--c-cream)", border: "1px solid var(--c-grey-100)" }}><i className="fa-solid fa-circle-check mr-1.5 text-[10px]" style={{ color: "var(--c-gold-deep)" }} /><strong>Requirement:</strong> {o.requirement}</span>}
        {o.salary && <span className="px-3 py-1.5 rounded-full text-xs" style={{ background: "var(--c-cream)", border: "1px solid var(--c-grey-100)" }}><i className="fa-solid fa-coins mr-1.5 text-[10px]" style={{ color: "var(--c-gold-deep)" }} /><strong>Pay:</strong> {o.salary}</span>}
      </div>

      <div className="prose max-w-none" data-testid="opp-body"><Markdownish md={o.body_markdown} /></div>

      {/* Apply CTA bar */}
      <div className="sticky bottom-0 left-0 right-0 -mx-4 md:mx-0 mt-12 mb-[-24px] md:mb-0 bg-white border-t md:border md:rounded-2xl md:shadow-sm p-4 md:p-6 flex flex-col md:flex-row md:items-center gap-3" style={{ borderColor: "var(--c-grey-100)" }} data-testid="apply-cta-bar">
        <div className="md:flex-1">
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: "var(--c-gold-deep)" }}>Ready to apply?</div>
          <div className="font-display text-lg md:text-xl mt-0.5">Takes about 2–3 minutes to complete.</div>
        </div>
        {closed ? (
          <div className="text-center md:text-right">
            <span className="inline-block px-4 py-2 rounded-md text-sm font-semibold" style={{ background: "var(--c-grey-100)", color: "var(--c-grey-500)" }} data-testid="apply-closed">Applications Closed</span>
          </div>
        ) : (
          <Link to={`/careers/${o.slug}/apply`} className="inline-flex items-center justify-center px-6 py-3 rounded-md font-semibold text-sm tracking-wide" style={{ background: "var(--c-black)", color: "#fff" }} data-testid="apply-now-btn">
            Apply Now <i className="fa-solid fa-arrow-right ml-2 text-[11px]" />
          </Link>
        )}
      </div>
    </CareersSection>
  );
}

/* -------------------------------------------------------------------------- */
/*  Application form                                                           */
/* -------------------------------------------------------------------------- */

function Field({ label, hint, required, children, name, error }) {
  return (
    <label className="block" htmlFor={name}>
      <span className="block text-sm font-semibold mb-1.5">
        {label}{required && <span className="ml-1" style={{ color: "var(--c-red)" }}>*</span>}
      </span>
      {children}
      {hint && <div className="mt-1 text-xs text-[color:var(--c-grey-500)]">{hint}</div>}
      {error && <div className="mt-1 text-xs" style={{ color: "var(--c-red)" }}>{error}</div>}
    </label>
  );
}

const INPUT_CLS = "w-full px-3.5 py-2.5 rounded-md border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--c-gold)]/40 focus:border-[color:var(--c-gold)]";
function TextInput(props) { return <input {...props} className={`${INPUT_CLS} ${props.className || ""}`} style={{ borderColor: "var(--c-grey-200, #E5E5E5)" }} />; }
function Textarea(props) { return <textarea {...props} className={`${INPUT_CLS} ${props.className || ""}`} style={{ borderColor: "var(--c-grey-200, #E5E5E5)" }} rows={props.rows || 4} />; }
function YesNo({ name, value, onChange, required }) {
  return (
    <div className="flex gap-2" role="radiogroup" aria-required={required}>
      {["yes", "no"].map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className="flex-1 px-3 py-2 rounded-md border text-sm font-semibold"
          style={{
            borderColor: value === v ? "var(--c-black)" : "var(--c-grey-100)",
            background: value === v ? "var(--c-black)" : "#fff",
            color: value === v ? "#fff" : "var(--c-black)",
          }}
          data-testid={`yn-${name}-${v}`}
          aria-pressed={value === v}
        >
          {v === "yes" ? "Yes" : "No"}
        </button>
      ))}
    </div>
  );
}

export function OpportunityApplyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.get(`${API}/careers/opportunities/${encodeURIComponent(slug)}`)
      .then((r) => setO(r.data))
      .catch(() => setErr("Opportunity not found or no longer available."));
  }, [slug]);

  if (err) return (
    <CareersSection>
      <PageHeader eyebrow="Careers" title="Opportunity not found" intro={<p>{err}</p>} />
      <Link to="/careers" className="inline-flex items-center text-sm font-semibold" style={{ color: "var(--c-gold-deep)" }}><i className="fa-solid fa-arrow-left mr-2 text-[11px]" />Back to Careers</Link>
    </CareersSection>
  );
  if (!o) return (<CareersSection><div className="h-10 w-56 bg-[color:var(--c-grey-100)] rounded animate-pulse" /></CareersSection>);
  if (o.status === "closed") {
    return (
      <CareersSection>
        <PageHeader eyebrow="Careers" title="Applications Closed" intro={<p>Applications for this opportunity are no longer accepted.</p>} />
        <Link to="/careers" className="inline-flex items-center text-sm font-semibold" style={{ color: "var(--c-gold-deep)" }}><i className="fa-solid fa-arrow-left mr-2 text-[11px]" />Back to Careers</Link>
      </CareersSection>
    );
  }
  return <ApplicationForm o={o} navigate={navigate} />;
}

function ApplicationForm({ o, navigate }) {
  const schema = o.form_schema || "opp1";
  const [state, setState] = useState({});
  const [files, setFiles] = useState({ cv: null });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverErr, setServerErr] = useState("");
  const [fieldErrs, setFieldErrs] = useState({});
  const formShownAt = useRef(Date.now());
  const submittedOnce = useRef(false);

  const set = (k) => (e) => setState((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const validate = () => {
    const errs = {};
    if (!state.full_name?.trim()) errs.full_name = "Required";
    if (!/.+@.+\..+/.test(state.email || "")) errs.email = "Enter a valid email";
    if (!state.phone?.trim()) errs.phone = "Required";
    if (!state.postcode?.trim()) errs.postcode = "Required";
    if (schema === "opp1") {
      if (!state.own_van) errs.own_van = "Required";
      if (!state.uk_licence) errs.uk_licence = "Required";
    }
    if (!files.cv) errs.cv = "Please attach your CV";
    if (!consent) errs.consent = "Please confirm consent";
    setFieldErrs(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submittedOnce.current) return;        // belt
    if (submitting) return;                   // braces
    if (!validate()) return;
    setSubmitting(true);
    submittedOnce.current = true;             // suspenders
    setServerErr("");
    try {
      const fd = new FormData();
      fd.append("opportunity_slug", o.slug);
      fd.append("full_name", state.full_name || "");
      fd.append("email", state.email || "");
      fd.append("phone", state.phone || "");
      fd.append("postcode", state.postcode || "");
      // Build answers payload (everything schema-specific)
      const answers = {};
      const copy = ["own_van", "uk_licence", "sales_exp", "delivery_exp", "b2b_exp", "experience_text", "areas",
                    "marketing_exp", "ecom_exp", "platforms_tools", "portfolio_link", "why_crown01"];
      copy.forEach((k) => { if (state[k] !== undefined && state[k] !== "") answers[k] = state[k]; });
      fd.append("answers_json", JSON.stringify(answers));
      fd.append("additional_message", state.additional_message || "");
      fd.append("privacy_consent", consent ? "true" : "false");
      fd.append("website", state.website || "");  // honeypot
      fd.append("form_shown_at", String(formShownAt.current));
      fd.append("cv", files.cv);

      const r = await axios.post(`${API}/careers/apply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r?.data?.ok) {
        navigate("/careers/apply/success", { replace: true });
        return;
      }
      throw new Error("Unexpected response");
    } catch (e) {
      submittedOnce.current = false;  // allow retry on failure
      const detail = e?.response?.data?.detail;
      setServerErr(typeof detail === "string" ? detail : "Could not submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CareersSection>
      <div className="mb-5">
        <Link to={`/careers/${o.slug}`} className="inline-flex items-center text-sm font-semibold" style={{ color: "var(--c-gold-deep)" }} data-testid="back-to-opp">
          <i className="fa-solid fa-arrow-left mr-2 text-[11px]" />Back to opportunity
        </Link>
      </div>
      <PageHeader eyebrow="Apply" title={<>Apply · <span className="text-[color:var(--c-grey-500)]">{o.title}</span></>} intro={<p>Takes 2–3 minutes. No account needed. We'll be in touch if your experience fits.</p>} />

      <form onSubmit={onSubmit} className="grid md:grid-cols-2 gap-5 md:gap-6 max-w-3xl" noValidate data-testid="application-form" encType="multipart/form-data">
        {/* Honeypot (hidden from humans) */}
        <input type="text" name="website" value={state.website || ""} onChange={set("website")} className="hidden" tabIndex="-1" autoComplete="off" aria-hidden="true" />

        <div className="md:col-span-2">
          <Eyebrow>Personal Information</Eyebrow>
        </div>
        <Field label="Full Name" required name="full_name" error={fieldErrs.full_name}>
          <TextInput id="full_name" name="full_name" value={state.full_name || ""} onChange={set("full_name")} autoComplete="name" data-testid="input-full-name" />
        </Field>
        <Field label="Email Address" required name="email" error={fieldErrs.email}>
          <TextInput id="email" type="email" name="email" value={state.email || ""} onChange={set("email")} autoComplete="email" data-testid="input-email" />
        </Field>
        <Field label="Phone Number" required name="phone" error={fieldErrs.phone}>
          <TextInput id="phone" type="tel" name="phone" value={state.phone || ""} onChange={set("phone")} autoComplete="tel" data-testid="input-phone" />
        </Field>
        <Field label="Postcode / Area" required name="postcode" error={fieldErrs.postcode}>
          <TextInput id="postcode" name="postcode" value={state.postcode || ""} onChange={set("postcode")} autoComplete="postal-code" data-testid="input-postcode" />
        </Field>

        {schema === "opp1" && (
          <>
            <div className="md:col-span-2 mt-2"><Eyebrow>Van &amp; Driving</Eyebrow></div>
            <Field label="Do you own or have regular access to a van?" required name="own_van" error={fieldErrs.own_van}>
              <YesNo name="own_van" value={state.own_van} onChange={(v) => set("own_van")(v)} required />
            </Field>
            <Field label="Do you hold a valid UK driving licence?" required name="uk_licence" error={fieldErrs.uk_licence}>
              <YesNo name="uk_licence" value={state.uk_licence} onChange={(v) => set("uk_licence")(v)} required />
            </Field>

            <div className="md:col-span-2 mt-2"><Eyebrow>Experience</Eyebrow></div>
            <Field label="Previous sales experience?" name="sales_exp"><YesNo name="sales_exp" value={state.sales_exp} onChange={(v) => set("sales_exp")(v)} /></Field>
            <Field label="Previous delivery experience?" name="delivery_exp"><YesNo name="delivery_exp" value={state.delivery_exp} onChange={(v) => set("delivery_exp")(v)} /></Field>
            <Field label="Previous B2B/business customer experience?" name="b2b_exp"><YesNo name="b2b_exp" value={state.b2b_exp} onChange={(v) => set("b2b_exp")(v)} /></Field>
            <div />
            <Field label="Tell us briefly about your previous experience" name="experience_text">
              <Textarea id="experience_text" value={state.experience_text || ""} onChange={set("experience_text")} rows={4} placeholder="Tell us about any previous delivery, sales, wholesale, retail or B2B experience and the types of products or services you have worked with." data-testid="input-experience" />
            </Field>
            <Field label="Which areas/postcodes would you be interested in covering?" name="areas">
              <TextInput id="areas" value={state.areas || ""} onChange={set("areas")} placeholder="e.g. N1, EN1, London East" data-testid="input-areas" />
            </Field>
          </>
        )}

        {schema === "opp2" && (
          <>
            <div className="md:col-span-2 mt-2"><Eyebrow>Experience</Eyebrow></div>
            <Field label="Previous marketing experience?" name="marketing_exp"><YesNo name="marketing_exp" value={state.marketing_exp} onChange={(v) => set("marketing_exp")(v)} /></Field>
            <Field label="Previous sales experience?" name="sales_exp"><YesNo name="sales_exp" value={state.sales_exp} onChange={(v) => set("sales_exp")(v)} /></Field>
            <Field label="Previous e-commerce experience?" name="ecom_exp"><YesNo name="ecom_exp" value={state.ecom_exp} onChange={(v) => set("ecom_exp")(v)} /></Field>
            <div />
            <Field label="Tell us briefly about your experience" name="experience_text">
              <Textarea id="experience_text" value={state.experience_text || ""} onChange={set("experience_text")} rows={4} placeholder="Tell us about your marketing, e-commerce, sales or content-creation experience." data-testid="input-experience" />
            </Field>
            <Field label="Which platforms or tools have you previously used?" hint="Shopify, Amazon, eBay, TikTok, Instagram, Meta, Canva, Google Ads, etc." name="platforms_tools">
              <TextInput id="platforms_tools" value={state.platforms_tools || ""} onChange={set("platforms_tools")} data-testid="input-platforms" />
            </Field>
            <Field label="Portfolio / LinkedIn / Social Media Link" name="portfolio_link">
              <TextInput id="portfolio_link" type="url" value={state.portfolio_link || ""} onChange={set("portfolio_link")} placeholder="https://" data-testid="input-portfolio" />
            </Field>
          </>
        )}

        <div className="md:col-span-2 mt-2"><Eyebrow>CV &amp; Message</Eyebrow></div>
        <Field label="Upload Your CV" required hint="PDF, DOC or DOCX · Max 5 MB" name="cv" error={fieldErrs.cv}>
          <div className="rounded-md border px-3 py-2.5 bg-white text-sm flex items-center gap-3" style={{ borderColor: "var(--c-grey-200, #E5E5E5)" }}>
            <input id="cv" name="cv" type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(e) => setFiles({ cv: e.target.files?.[0] || null })} className="block w-full text-sm" data-testid="input-cv" />
          </div>
          {files.cv && <div className="mt-1 text-xs text-[color:var(--c-grey-500)]"><i className="fa-solid fa-paperclip mr-1" />{files.cv.name} · {(files.cv.size / 1024).toFixed(0)} KB</div>}
        </Field>
        <Field label={schema === "opp2" ? "Why would you like to work with Crown01?" : "Anything else you'd like us to know?"} name="additional_message">
          <Textarea id="additional_message" value={state.additional_message || ""} onChange={set("additional_message")} rows={3} data-testid="input-message" />
        </Field>

        <div className="md:col-span-2 mt-2">
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" data-testid="input-consent" />
            <span>
              I confirm that the information provided is correct and I agree to Crown01 processing my information for the purpose of reviewing my application. See our <Link to="/privacy" className="font-semibold underline" style={{ color: "var(--c-gold-deep)" }}>Privacy Policy</Link>.
              {fieldErrs.consent && <span className="block mt-1" style={{ color: "var(--c-red)" }}>{fieldErrs.consent}</span>}
            </span>
          </label>
        </div>

        {serverErr && <div className="md:col-span-2 rounded-md p-3 text-sm" style={{ background: "rgba(220,38,38,0.08)", color: "var(--c-red)" }} data-testid="apply-server-error">{serverErr}</div>}

        <div className="md:col-span-2 mt-4">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center w-full md:w-auto px-7 py-3.5 rounded-md font-semibold tracking-wide text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: "var(--c-black)", color: "#fff" }}
            data-testid="submit-application"
          >
            {submitting ? (<><i className="fa-solid fa-spinner fa-spin mr-2" />Submitting…</>) : (<>Submit Application <i className="fa-solid fa-paper-plane ml-2 text-[11px]" /></>)}
          </button>
        </div>
      </form>
    </CareersSection>
  );
}

export function ApplicationSuccessPage() {
  return (
    <CareersSection>
      <div className="max-w-xl mx-auto text-center">
        <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "var(--c-cream)", border: "1px solid var(--c-gold)" }}>
          <i className="fa-solid fa-check text-xl" style={{ color: "var(--c-gold-deep)" }} />
        </div>
        <Eyebrow>Application Received</Eyebrow>
        <h1 className="font-display text-3xl md:text-4xl mt-2" data-testid="apply-success-title">Thank you for your interest in Crown01.</h1>
        <p className="mt-4 text-[color:var(--c-grey-700)] leading-relaxed">We have received your application successfully. Our team will review your information and will contact you if your experience is suitable for the opportunity.</p>
        <p className="mt-2 text-sm text-[color:var(--c-grey-500)]">There is no need to submit your application again.</p>
        <div className="mt-8">
          <Link to="/careers" className="inline-flex items-center justify-center px-6 py-3 rounded-md font-semibold text-sm tracking-wide" style={{ background: "var(--c-black)", color: "#fff" }} data-testid="return-to-careers">
            Return to Careers<i className="fa-solid fa-arrow-right ml-2 text-[11px]" />
          </Link>
        </div>
      </div>
    </CareersSection>
  );
}
